import React from "react"
import { Progress, Tag, Tooltip } from "antd"
import { WarningOutlined } from "@ant-design/icons"

import Strings from "utils/strings"
import {
	ExternalReturnEntry,
	ExternalReturnState,
	isExternalReturnWarning,
} from "../externalReturns"

import "./ExternalReturnsProgress.scss"

const RETURN_TAGS: Record<
	ExternalReturnState,
	{ color: string; label: () => string }
> = {
	returning: {
		color: "gold",
		label: () => Strings.routes.externalReturnsReturning,
	},
	returned: {
		color: "green",
		label: () => Strings.routes.externalReturnsReturned,
	},
	noReturn: {
		color: "red",
		label: () => Strings.routes.externalReturnsNoReturn,
	},
	returnFailed: {
		color: "red",
		label: () => Strings.routes.externalReturnsReturnFailed,
	},
}

/** Where one order's parcel is, in the same colours as the progress bar. */
export const ExternalReturnTag: React.FC<{
	state: ExternalReturnState | null
	style?: React.CSSProperties
}> = ({ state, style }) => {
	if (!state) return null
	const { color, label } = RETURN_TAGS[state]
	return (
		<Tag
			className="ExternalReturnTag"
			color={color}
			icon={isExternalReturnWarning(state) ? <WarningOutlined /> : undefined}
			style={{ margin: 0, ...style }}
		>
			{label()}
		</Tag>
	)
}

const formatFailedAt = (value?: string) => {
	if (!value) return null
	const date = new Date(value)
	if (isNaN(date.getTime())) return null
	return date.toLocaleString([], {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	})
}

const WarningList: React.FC<{ warnings: ExternalReturnEntry[] }> = ({
	warnings,
}) => (
	<div className="ExternalReturnsProgress__warnings">
		<strong>
			{Strings.routes.externalReturnsWarning.replace(
				"{0}",
				String(warnings.length)
			)}
		</strong>
		{warnings.map((entry) => {
			const failedAt = formatFailedAt(entry.failedAt)
			return (
				<div key={entry.orderId} className="ExternalReturnsProgress__warning">
					<strong>{entry.orderId}</strong>
					{entry.barcode && entry.barcode !== entry.orderId && (
						<span> · {entry.barcode}</span>
					)}
					{entry.reason && <span> · {entry.reason}</span>}
					{failedAt && (
						<span>
							{" "}
							· {Strings.routes.externalReturnsFailedAt} {failedAt}
						</span>
					)}
					<span>
						{" "}
						·{" "}
						{entry.state === "returnFailed"
							? Strings.routes.externalReturnsReturnFailed
							: Strings.routes.externalReturnsNoReturn}
					</span>
				</div>
			)
		})}
	</div>
)

/**
 * How many of a batch's not-delivered parcels are back. The ones that will not
 * come back on their own raise a flashing sign; hovering it lists them.
 */
const ExternalReturnsProgress: React.FC<{
	entries: ExternalReturnEntry[]
	style?: React.CSSProperties
	// Lets the label match the other labels of whatever card it sits in
	labelClassName?: string
}> = ({ entries, style, labelClassName }) => {
	if (entries.length === 0) return null

	const returned = entries.filter((e) => e.state === "returned").length
	const warnings = entries.filter((e) => isExternalReturnWarning(e.state))

	return (
		<div className="ExternalReturnsProgress" style={style}>
			<span className={labelClassName ?? "ExternalReturnsProgress__label"}>
				{Strings.routes.externalReturnsProgress}
			</span>
			<Progress
				className="ExternalReturnsProgress__bar"
				percent={(returned / entries.length) * 100}
				size="small"
				showInfo={false}
				// Stays primary at 100%: antd would switch a full bar to the theme's success colour
				status="normal"
			/>
			<span className="ExternalReturnsProgress__count">
				{returned}/{entries.length}
			</span>
			{warnings.length > 0 && (
				<Tooltip
					title={<WarningList warnings={warnings} />}
					zIndex={2000}
					overlayStyle={{ maxWidth: 420 }}
				>
					<WarningOutlined
						className="ExternalReturnsProgress__danger"
						aria-label={Strings.routes.externalReturnsWarning.replace(
							"{0}",
							String(warnings.length)
						)}
					/>
				</Tooltip>
			)}
		</div>
	)
}

export default ExternalReturnsProgress
